import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "dental-web-design-colorado",
  metaTitle: "Dental Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom dental web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Dental marketing", to: "/dental-marketing-colorado" },
  ],
  currentLabel: "Dental Web Design Colorado",
  eyebrow: "Dental Web Design — Colorado",
  h1: (
    <>
      Dental <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded dental websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Dental websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first dental sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Dental web design in Colorado",
    body: "Dental web design is the practice of building conversion-focused, mobile-first websites for dental companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — new-patient searches happen on phones during lunch breaks." },
    { title: "Online booking integration", body: "Direct integration with NexHealth, LocalMed, or your PMS for real-time booking from the site." },
    { title: "Service pages per treatment", body: "Implants, Invisalign, veneers, emergency, sedation, pediatric — each ranks for its own queries." },
    { title: "HIPAA-aware forms", body: "New-patient forms scoped to non-PHI intake with secure handoff to your practice management system." },
    { title: "Before/after galleries", body: "Properly disclosed cosmetic galleries with schema for image SEO." },
    { title: "Schema and SEO at launch", body: "Dentist, MedicalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "New patients judge in 4 seconds", body: "A slow site or generic stock-photo homepage loses the lead to the practice next door." },
    { heading: "Online booking doubles conversion", body: "Practices with real-time booking convert 2–3x more web visitors than form-only or call-only practices." },
    { heading: "Reviews and trust are the close", body: "We surface Google reviews, doctor bios, and tour photos above the fold — not buried on an \"about\" page." },
  ],
  relatedLinks: [
    { to: "/dental-marketing-colorado", label: "Dental marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/dental-seo-colorado", label: "Dental SEO in Colorado" },
    { to: "/dental-google-maps-colorado", label: "Dental Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado dental case studies" },
    { to: "/wix-vs-custom-website-colorado-dentist", label: "Wix vs custom website for Dentist" },
    { to: "/squarespace-vs-custom-website-colorado-dentist", label: "Squarespace vs custom website for Dentist" },
    { to: "/godaddy-vs-custom-website-colorado-dentist", label: "GoDaddy vs custom website for Dentist" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a dental website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a dental website?", a: "Typical timeline is 4–6 weeks for a 12–20 page dental site with full service and service-area pages. Larger multi-location dental companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs dental companies?", a: "No — we build dental sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Dental Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for dental companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/dental-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
